import React from 'react';
import {useTranslation} from 'react-i18next';

import {isNil, last} from 'lodash';

import {useOnChange} from '@edna/hooks';

import ChevronLeftIcon from './icons/chevronLeft.svg';
import ChevronRightIcon from './icons/chevronRight.svg';
import * as S from './style';
import {usePageNavigation} from './usePageNavigation';

export enum ECommonSearchParams {
  START = 'start',
  LIMIT = 'limit',
}

export type TProps = {
  pagesNumber: number;
  totalItemsNumber?: number;
  itemsPerPageOptions?: number[];
  maxPaginationItems?: number;
  paginationGroupSize?: number;
  titleKey?: string;
  itemsPerPageKey?: string;
  onPageChange: (activePageIndex: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  getPageNumber?: (pagesNumber: number) => number;
  itemsPerPageValue?: number;
  currentPage?: number;
};

export const getPageNumberFromParams = (pagesNumber: number): number => {
  const searchParams = new URLSearchParams(window.location.search);
  const startParam = searchParams.getAll(ECommonSearchParams.START);
  const initialPageNumber = Number(last(startParam)) || 0;

  if (initialPageNumber > pagesNumber - 1) {
    return pagesNumber - 1;
  }

  return Math.max(initialPageNumber, 0);
};

export const PageNavigation: React.FC<TProps> = ({
  pagesNumber,
  maxPaginationItems,
  paginationGroupSize,
  titleKey = 'EdnaPageNavigation:total',
  itemsPerPageKey = 'EdnaPageNavigation:itemsPerPage',
  onPageChange,
  totalItemsNumber,
  itemsPerPageOptions,
  onItemsPerPageChange,
  getPageNumber = getPageNumberFromParams,
  itemsPerPageValue,
  currentPage,
}) => {
  const {t} = useTranslation();

  const {
    getPaginationArray,
    prevIsDisabled,
    nextIsDisabled,
    activePageNumber,
    next,
    prev,
    goToPage,
  } = usePageNavigation({
    pagesNumber,
    maxPaginationItems,
    paginationGroupSize,
    getPageNumberFromParams: isNil(currentPage) ? getPageNumber : undefined,
    callback: onPageChange,
    currentPage,
  });
  const [itemsPerPage, setItemsPerPage] = React.useState(
    itemsPerPageValue ?? itemsPerPageOptions?.[0],
  );

  useOnChange(() => {
    setItemsPerPage(itemsPerPageValue);
  }, [itemsPerPageValue]);

  const handleItemsPerPageChange = React.useCallback(
    (value: number) => {
      setItemsPerPage(value);
      onItemsPerPageChange?.(value);
    },
    [onItemsPerPageChange],
  );

  return (
    <S.PageNavigationWrapper>
      <S.TotalItems>
        {totalItemsNumber && (
          <>
            <S.TotalItemsLabel>{t(titleKey)}</S.TotalItemsLabel>:&nbsp;
            <S.TotalItemsValue>{totalItemsNumber}</S.TotalItemsValue>
          </>
        )}
      </S.TotalItems>

      <S.PageNavigation>
        {pagesNumber > 1 && (
          <>
            <S.PageNavigationControl square disabled={prevIsDisabled} onClick={prev}>
              <ChevronLeftIcon />
            </S.PageNavigationControl>
            {getPaginationArray().map((item, index) => (
              <S.PageNavigationButton
                key={`${index}_${item}`}
                square
                active={item === activePageNumber}
                disabled={item === activePageNumber || item === '...'}
                onClick={() => item !== '...' && goToPage(item - 1)}
              >
                {item}
              </S.PageNavigationButton>
            ))}
            <S.PageNavigationControl square disabled={nextIsDisabled} onClick={next}>
              <ChevronRightIcon />
            </S.PageNavigationControl>
          </>
        )}
      </S.PageNavigation>

      <S.ItemsPerPage>
        {itemsPerPageOptions && (
          <>
            {t(itemsPerPageKey)}
            <S.ItemsPerPageSelect
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              options={itemsPerPageOptions.map((option) => ({value: option, label: option}))}
              renderLabel={({option}) => (
                <S.ItemsPerPageLabel key={option?.value}>{option?.label}</S.ItemsPerPageLabel>
              )}
              placementY="top"
            />
          </>
        )}
      </S.ItemsPerPage>
    </S.PageNavigationWrapper>
  );
};
